import { useInkind } from '@gik/api/inkinds/inkind';
import { useWishlistProducts } from '@gik/api/inkinds/wishlistProducts';
import { useInkindCan } from '@gik/core/store/permissions';
import bemBlock from '@gik/core/utils/bemBlock';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { ProductTileToolbar } from '@gik/ui/gik/ProductTile';
import { Grid } from '@gik/ui/Grid';
import { FrameBone, ParagraphBone } from '@gik/ui/SkeletonLoader';
import { WishlistSidebarSection } from '@gik/wishlist-page/components/WishlistSidebarSection';
import React from 'react';
import SeeFullWishlistButton from './SeeFullWishlistButton';
import { WishlistEmptyState } from './WishlistEmptyState';
import { WishlistProductTile } from './WishlistProductTile';

export interface IWishlistGridProps {
  inkindRouteId: string;
  isFullWishlistPage?: boolean;
  skeletonItems?: number;
  cols?: number;
}

const skelBem = bemBlock(`wishlist-grid-skeleton`);
const Skeleton = ({ skeletonItems = 4 }: Partial<IWishlistGridProps>) => (
  <Grid className={`${skelBem()} gik-grid--4-cols`}>
    {Array.from(Array(skeletonItems).keys()).map(index => {
      return (
        <div key={index} className={skelBem('item')}>
          <FrameBone className={skelBem('image')} />
          <main>
            <ParagraphBone words={18} className={skelBem('text')} />
          </main>
        </div>
      );
    })}
  </Grid>
);

function WishlistGridComp({ inkindRouteId, isFullWishlistPage, cols }: IWishlistGridProps): React.ReactElement {
  const bem = bemBlock('wishlist-grid');
  const { data: inkindPage, error: inkindPageError } = useInkind(inkindRouteId);
  const canEdit = useInkindCan('manage', inkindRouteId, inkindPage?.groupId);
  const maxProductsToDisplay = isFullWishlistPage ? undefined : 24;

  const {
    data: items,
    error: wishListProductsError,
    isValidating: isLoadingWishlistProducts,
  } = useWishlistProducts(inkindRouteId);

  const isLoading = !items || !inkindPage;
  const isError = isLoading && (inkindPageError || wishListProductsError);

  if (isLoading || isError) {
    return <Skeleton />;
  }

  const hasItems = items?.length > 0;

  return (
    <WishlistSidebarSection inkindRouteId={inkindRouteId} className={bem()}>
      <Grid className={bem(null, null)} cols={cols}>
        {items?.slice(0, maxProductsToDisplay)?.map(item => (
          <WishlistProductTile
            key={item.id}
            item={item}
            inkindRouteId={inkindRouteId}
            toolbar={
              <ProductTileToolbar
                inkindRouteId={inkindRouteId}
                itemId={item.id}
                hasRemoveFromCurrentPageButton={canEdit}
              />
            }
          />
        ))}
      </Grid>
      {!isLoadingWishlistProducts && !hasItems && <WishlistEmptyState inkindRouteId={inkindRouteId} proportion={0.8} />}
      <div className={bem('grid-footer')}>
        {maxProductsToDisplay && maxProductsToDisplay < items?.length && (
          <SeeFullWishlistButton inkindPage={inkindPage} />
        )}
      </div>
    </WishlistSidebarSection>
  );
}

export const WishlistGrid = withComponentErrorBoundary(WishlistGridComp);
